import React, { FC, useCallback, useMemo } from "react";
import { BuilderActionInput } from "@src/types.generated";
import usePollServerForBuilderActions from "@src/features/libraryItem/hooks/usePollServerForBuilderActions";
import useSyncBuilderActionsToServer, {
  useSyncBuilderActionsStore,
} from "@src/features/libraryItem/hooks/useSyncBuilderActionsToServer";
import { useEnqueueBuilderAction } from "@src/features/builders/shared/autosave/builderActionsSync";
import { BuilderActionFragment } from "@src/features/libraryItem/hooks/usePollServerForBuilderActions.generated";
import { cloneDeep } from "lodash";
import shouldActionsBeDeduped from "@src/features/libraryItem/module/utils/shouldActionsBeDeduped";
import { useUndoRedoKeystrokes } from "@src/features/builders/shared/undoRedo/useUndoRedoKeystrokes";
import { useRouter } from "next/router";
import { uuid4 } from "@src/lib/utils/strings";
import { atom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { selectedContentIdsAtom } from "@src/features/libraryItem/course/atoms";

export interface BuilderActionContextState {
  dispatchAction: (
    contentIds: BuilderActionContentIds,
    payload: ActionPayload | ActionPayload[],
    skipAddToUndoStack?: boolean,
  ) => void;
  undoAction: () => void;
  redoAction: () => void;
}

export const BuilderActionContext = React.createContext<
  BuilderActionContextState | undefined
>(undefined);

export type BuilderActionContentIds = {
  moduleId?: number;
  courseId?: number;
  skillId?: number;
  trainingResourceId?: string;
};

export type BuilderActionContextContent = {
  contentIds: BuilderActionContentIds;
  initialLastAppliedBuilderActionId: number | null | undefined;
};

export type BuilderActionContextProviderProps = {
  content: BuilderActionContextContent[];
  applyActionFromServer: (action: BuilderActionPartial) => void;
  applyActionFromClient: (action: BuilderActionPartial) => void;
  children: React.ReactNode;
  disableZKeystrokeHandling?: boolean;
  /**
   * Whether to keep polling the server for other clients' actions. Off while
   * the editor is not on screen (a read-only tab of the same page), where
   * nothing renders what the poll would bring — queued local actions still
   * sync out either way.
   */
  pollServerActions?: boolean;
};

export type BuilderActionPartial = Pick<
  BuilderActionFragment,
  "addedModuleContentMembership"
> & {
  payload: Omit<BuilderActionInput, "clientId" | "uuid">;
  timestamp: Date;
};

export type ActionPayload = {
  action: BuilderActionPartial;
  inverseAction: BuilderActionPartial | null;
};

type UndoStackItemContainer = {
  uuid: string;
  contentIds: BuilderActionContentIds;
  undoStackItems: ActionPayload[];
  url: string;
};

type State = {
  undoStack: UndoStackItemContainer[];
  redoStack: UndoStackItemContainer[];
};
const stateAtom = atom<State>({
  undoStack: [],
  redoStack: [],
});
const stateForSelectedContentAtom = atom((get) => {
  const state = get(stateAtom);
  const selectedContentIds = get(selectedContentIdsAtom);
  return {
    undoStack: state.undoStack.filter((item) => {
      return (
        item.contentIds.moduleId ||
        contentIdsAreEqual(item.contentIds, selectedContentIds)
      );
    }),
    redoStack: state.redoStack.filter((item) => {
      return (
        item.contentIds.moduleId ||
        contentIdsAreEqual(item.contentIds, selectedContentIds)
      );
    }),
  };
});

export const undoAvailableCountAtom = atom(
  (get) => get(stateForSelectedContentAtom).undoStack.length,
);
export const redoAvailableCountAtom = atom(
  (get) => get(stateForSelectedContentAtom).redoStack.length,
);

const BuilderActionContextProvider: FC<BuilderActionContextProviderProps> = (
  props,
) => {
  useSyncBuilderActionsToServer();
  const enqueue = useEnqueueBuilderAction(useSyncBuilderActionsStore);
  const applyActionFromClient = props.applyActionFromClient;

  const pollServerActions = props.pollServerActions ?? true;
  const pollArgs = useMemo(
    () => ({
      applyActionFromServerFn: props.applyActionFromServer,
      // No content to poll for is how the hook is told to stand down.
      contentToPollActionsFor: pollServerActions ? props.content : [],
    }),
    [props.applyActionFromServer, props.content, pollServerActions],
  );

  usePollServerForBuilderActions(pollArgs);
  const router = useRouter();
  const addDedupedItemToUndoStack = useAtomCallback(
    useCallback(
      (get, set, item: UndoStackItemContainer) => {
        set(stateAtom, (prevState) => {
          const newUndoStackItem = cloneDeep(item);
          const newState = cloneDeep(prevState);
          newState.redoStack = [];
          const prevUndoStackItem =
            newState.undoStack[newState.undoStack.length - 1];
          if (
            prevUndoStackItem &&
            shouldMergeUndoStackItems(prevUndoStackItem, newUndoStackItem)
          ) {
            const mergedPrevAndNewUndoStackItem: UndoStackItemContainer = {
              uuid: newUndoStackItem.uuid,
              contentIds: newUndoStackItem.contentIds,
              undoStackItems: [
                mergeUndoRedoActions(
                  prevUndoStackItem?.undoStackItems[0],
                  newUndoStackItem.undoStackItems[0],
                ),
              ],
              url: router.asPath,
            };
            newState.undoStack.pop();
            newState.undoStack.push(mergedPrevAndNewUndoStackItem);
          } else {
            newState.undoStack.push(newUndoStackItem);
          }
          return newState;
        });
      },
      [router.asPath],
    ),
  );
  const dispatchAction: BuilderActionContextState["dispatchAction"] =
    useCallback(
      (contentIds, item, skipAddToUndoStack) => {
        const undoStackItems = Array.isArray(item) ? item : [item];
        undoStackItems.forEach((x) => {
          enqueue(x.action);
          applyActionFromClient(x.action);
        });
        if (skipAddToUndoStack) return;
        addDedupedItemToUndoStack({
          uuid: uuid4(),
          contentIds,
          undoStackItems,
          url: router.asPath,
        });
      },
      [
        addDedupedItemToUndoStack,
        enqueue,
        applyActionFromClient,
        router.asPath,
      ],
    );
  const undoAction: BuilderActionContextState["undoAction"] = useAtomCallback(
    useCallback(
      (get, set) => {
        const state = get(stateAtom);
        const newState = cloneDeep(state);
        const stateForSelectedContent = get(stateForSelectedContentAtom);
        const undoItem =
          stateForSelectedContent.undoStack[
            stateForSelectedContent.undoStack.length - 1
          ];
        if (!undoItem) return;
        if (router.asPath !== undoItem.url) {
          // @ts-ignore
          router.push(undoItem.url);
        }
        [...undoItem.undoStackItems].reverse().forEach((x) => {
          if (x.inverseAction) {
            enqueue(x.inverseAction);
            applyActionFromClient(x.inverseAction);
          }
        });
        newState.undoStack = newState.undoStack.filter(
          (x) => x.uuid !== undoItem.uuid,
        );
        newState.redoStack.push(undoItem);
        set(stateAtom, newState);
      },
      [enqueue, applyActionFromClient, router],
    ),
  );
  const redoAction: BuilderActionContextState["redoAction"] = useAtomCallback(
    useCallback(
      (get, set) => {
        const state = get(stateAtom);
        const newState = cloneDeep(state);
        const stateForSelectedContent = get(stateForSelectedContentAtom);
        const redoItem =
          stateForSelectedContent.redoStack[
            stateForSelectedContent.redoStack.length - 1
          ];
        if (!redoItem) return;
        if (router.asPath !== redoItem.url) {
          // @ts-ignore
          router.push(redoItem.url);
        }
        redoItem.undoStackItems.forEach((x) => {
          enqueue(x.action);
          applyActionFromClient(x.action);
        });
        newState.redoStack = newState.redoStack.filter(
          (x) => x.uuid !== redoItem.uuid,
        );
        newState.undoStack.push(redoItem);
        set(stateAtom, newState);
      },
      [enqueue, applyActionFromClient, router],
    ),
  );
  useUndoRedoKeystrokes({
    undo: undoAction,
    redo: redoAction,
    enabled: !props.disableZKeystrokeHandling,
  });

  const contextState: BuilderActionContextState = useMemo(() => {
    return {
      dispatchAction,
      undoAction,
      redoAction,
    };
  }, [dispatchAction, redoAction, undoAction]);
  return (
    <BuilderActionContext.Provider value={contextState}>
      {props.children}
    </BuilderActionContext.Provider>
  );
};

export default BuilderActionContextProvider;

const shouldMergeUndoStackItems = (
  prevItem: UndoStackItemContainer,
  nextItem: UndoStackItemContainer,
): boolean => {
  const prevItemFirstAction = prevItem.undoStackItems[0];
  const nextItemFirstAction = nextItem.undoStackItems[0];
  if (
    !prevItemFirstAction ||
    !nextItemFirstAction ||
    prevItem.undoStackItems.length > 1 ||
    nextItem.undoStackItems.length > 1
  )
    return false;
  return shouldActionsBeDeduped(
    prevItemFirstAction.action,
    nextItemFirstAction.action,
  );
};

const mergeUndoRedoActions = (
  prevUndoItem: ActionPayload,
  nextUndoItem: ActionPayload,
): ActionPayload => {
  return {
    action: nextUndoItem.action,
    inverseAction: prevUndoItem.inverseAction,
  };
};

const contentIdsAreEqual = (
  a: BuilderActionContentIds,
  b: BuilderActionContentIds,
): boolean => {
  if (a.moduleId && b.moduleId) return a.moduleId === b.moduleId;
  else if (a.courseId && b.courseId) return a.courseId === b.courseId;
  else if (a.skillId && b.skillId) return a.skillId === b.skillId;
  else if (a.trainingResourceId && b.trainingResourceId)
    return a.trainingResourceId === b.trainingResourceId;
  else return false;
};
